/**
 * Le panneau d'acheminement — qui apporte un matériau, et d'où — **partagé par ses trois
 * porteurs**.
 *
 * *« C'est exactement les mêmes champs que sur une pastille de transport »* (métier,
 * 21/08/2026), dit d'abord de la pastille de livraison (migration 039) puis de la ligne de
 * contre-étude (migration 041). Trois endroits où la question se pose :
 *
 *   - la **ligne de contre-étude** — ce que le chiffrage décide, valeur de départ ;
 *   - la **pastille de livraison** — ce volume-là, ce jour-là ;
 *   - la **pastille de transport** — cette rotation-là.
 *
 * **Un seul composant, et c'est le point.** Le retour du métier du 13/08 parlait de la
 * provenance *des matériaux* ; il n'a d'abord été appliqué qu'à la rotation de camion, ce qui
 * a laissé la pastille du matériau sans champ « qui livre » pendant huit jours. Trois panneaux
 * jumeaux finiraient par ne plus proposer les mêmes modes — la même panne, un cran plus loin.
 */
import { useState } from "react";
import type { JSX } from "react";
import { expliquerLAbsenceDeKm, formaterKm } from "@planif/moteur";

import type { Carriere } from "./api";
import { ChoixCommune } from "./ChoixCommune";

/** Les trois modes d'acheminement, dans l'ordre où le métier les a énoncés. */
export const MODES_LIVRAISON: readonly { readonly code: string; readonly libelle: string }[] = [
  { code: "fournisseur", libelle: "par le fournisseur" },
  { code: "transporteur_externe", libelle: "transporteur externe" },
  { code: "interne", libelle: "par nos soins" },
];

/**
 * Un petit champ de texte qui n'enregistre qu'au départ du curseur ou à Entrée.
 *
 * **Pas d'écriture à la frappe** : saisir « 100 » passerait par « 1 » puis « 10 », donc trois
 * écritures et trois lignes de journal pour une décision.
 *
 * `Échap` reprend la valeur connue. Une saisie invalide ne s'enregistre pas et n'efface rien :
 * le champ revient simplement à ce que la base porte, et le liseré rouge l'a dit pendant la
 * frappe.
 */
export function ChampCourt({
  valeur,
  largeur,
  motif,
  invite,
  titre,
  modifiable,
  enAttente,
  /** Vrai quand vider le champ est une réponse légitime — l'heure indifférente. */
  videAutorise = false,
  onValider,
}: {
  valeur: string;
  largeur: string;
  motif: RegExp;
  invite: string;
  titre: string;
  modifiable: boolean;
  enAttente: boolean;
  videAutorise?: boolean;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const [saisie, setSaisie] = useState<string | null>(null);
  const affiche = saisie ?? valeur;
  const brut = affiche.trim();
  const valide = brut === "" ? videAutorise : motif.test(brut);

  const enregistrer = (): void => {
    if (saisie === null) return;
    setSaisie(null);
    const propre = saisie.trim();
    if (propre === valeur.trim()) return;
    if (propre === "" ? !videAutorise : !motif.test(propre)) return;
    onValider(propre);
  };

  return (
    <input
      className={`champ-court${valide ? "" : " champ-court--invalide"}`}
      style={{ width: largeur }}
      value={affiche}
      placeholder={invite}
      title={titre}
      disabled={!modifiable || enAttente}
      onChange={(e) => {
        setSaisie(e.target.value);
      }}
      onBlur={enregistrer}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") setSaisie(null);
      }}
    />
  );
}

/**
 * La distance entre les deux villes — **déduite, jamais saisie**.
 *
 * *« Si tu trouves une ville de départ et une ville de livraison qui correspond dans nos
 * fichiers distance alors tu fais apparaître la distance en km dans un nouveau champ km »*
 * (Chris, 08/09/2026).*
 *
 * ## Vide, en disant pourquoi
 *
 * Le champ n'est pas modifiable et ne porte pas de zéro quand la distance manque : *tranché
 * par Chris le 08/09/2026 après la mesure — les sept tables ne donnent que des distances
 * DEPUIS nos sites, et il n'existe aucune distance commune → commune.* Un trajet
 * fournisseur → chantier n'en a donc aucune, et un 0 se lirait « c'est à côté ». C'est la
 * règle de la 058 : une distance inconnue ne déduit aucune zone, bien qu'elle affiche 0.
 *
 * L'infobulle porte la raison, en toutes lettres et pas en code : sans elle, un champ vide
 * passe pour une panne, et on cherche la ville qu'on vient de choisir.
 */
export function Kilometrage({
  km,
  motif,
  estForce = false,
  kmDeduit = null,
  modifiable = false,
  enAttente = false,
  onForcer,
}: {
  readonly km: string | null;
  readonly motif: string | null;
  /** Vrai quand le km affiché a été SAISI et non déduit (073). */
  readonly estForce?: boolean;
  /** Ce que la table dit, quand un km saisi la contredit. */
  readonly kmDeduit?: string | null;
  readonly modifiable?: boolean;
  readonly enAttente?: boolean;
  /** Absent = le champ reste en lecture, comme avant la 073. */
  readonly onForcer?: (km: string | null) => void;
}): JSX.Element {
  /*
    LE CHAMP DE SAISIE, quand l'écran l'autorise (073).

    *« Le responsable transport devra renseigner manuellement le km. Tu devras donc permettre
    de forcer le km »* (Chris, 08/09/2026). Il est offert **dans tous les cas**, pas seulement
    quand la déduction manque : *« dans les autres cas de mode de livraison […] forcer le km si
    on estime que c'est important »*. Un champ qui n'apparaîtrait qu'en l'absence de déduction
    disparaîtrait au moment où l'on veut corriger une distance jugée fausse.

    La valeur affichée est celle qui compte — déduite ou saisie —, et l'infobulle dit laquelle,
    plus ce que la table répond quand les deux diffèrent. Sans cela, un km saisi il y a un mois
    resterait présenté comme une vérité le jour où la table est complétée.
  */
  if (onForcer !== undefined) {
    const explication = estForce
      ? "Kilométrage SAISI à la main : il l'emporte sur les tables de distances." +
        (kmDeduit === null
          ? " Aucune table ne répond pour ce trajet."
          : ` La table du site, elle, donne ${formaterKm(kmDeduit)} km.`) +
        " Vider le champ revient à la déduction."
      : km !== null
        ? "Distance figée pour ce chantier, recopiée des tables de distances quand la ville a été choisie (093). La saisir ici la remplace, sans toucher aux tables."
        : (expliquerLAbsenceDeKm(motif) ?? "Saisir le kilométrage de ce trajet.");
    return (
      <span className={`km-saisie${estForce ? " km-saisie--forcee" : ""}`}>
        <ChampCourt
          valeur={km === null ? "" : formaterKm(km)}
          largeur="4.5rem"
          motif={/^\d{1,6}([.,]\d{1,2})?$/}
          invite="km ?"
          videAutorise
          titre={explication}
          modifiable={modifiable}
          enAttente={enAttente}
          onValider={(v) => {
            onForcer(v === "" ? null : v);
          }}
        />
        <span className="km-saisie__unite" title={explication}>
          km{estForce ? " ·" : ""}
        </span>
        {/* La marque du forçage, comme « chantier » sur une ville reprise : l'écran dit d'où
            vient ce qu'il montre, sinon une saisie et une déduction se lisent pareil. */}
        {estForce && (
          <span className="km-saisie__forcee" title={explication}>
            saisi
          </span>
        )}
      </span>
    );
  }

  if (km !== null) {
    // La règle d'affichage — une décimale, zéro final retiré — vit dans le moteur, avec la
      // mesure qui l'a fixée : elle vaudra pour tous les écrans qui montreront un km.
    return (
      <span
        className="km"
        title={
          estForce
            ? "Kilométrage saisi à la main (073)."
            : "Distance figée pour ce chantier (093)."
        }
      >
        {formaterKm(km)} km{estForce ? " (saisi)" : ""}
      </span>
    );
  }
  const pourquoi = expliquerLAbsenceDeKm(motif);
  // Aucun motif : le porteur vient d'un contexte qui ne calcule pas le km. Rien ne
  // s'affiche plutôt qu'un tiret sans explication.
  if (pourquoi === null) return <span className="km km--muet" />;
  return (
      <span className="km km--absent" title={pourquoi}>
        km ?
      </span>
  );
}

/**
 * Ce que les trois porteurs ont en commun : d'où ça part, et qui l'apporte.
 *
 * Une forme structurelle plutôt que l'union des trois types : le panneau ne lit que ces champs,
 * et les nommer ici dit exactement ce dont il dépend.
 *
 * Les quatre derniers — les deux noms de villes, leurs codes postaux, le km et son motif — sont
 * DÉDUITS par le serveur et ne se saisissent pas : le panneau les affiche, il ne les renvoie
 * jamais (voir `ChangementAcheminement`, qui ne porte que les identifiants de commune).
 */
export type PorteurDAcheminement = {
  readonly duGroupe: boolean;
  readonly carriereId: string | null;
  readonly modeLivraison: string | null;
  readonly fournisseur: string | null;
  /** La ville de CHARGEMENT, par son identifiant de commune (072). Liste fermée. */
  readonly communeProvenanceId: string | null;
  /** La ville de CHARGEMENT saisie, hors référentiel des distances (073). */
  readonly villeProvenanceLibre: string | null;
  /** La ville de LIVRAISON, au même référentiel (072). */
  readonly communeLivraisonId: string | null;
  /** Déduits du référentiel par le serveur — ils ne se saisissent pas. */
  readonly villeProvenance: string | null;
  readonly codePostalProvenance: string | null;
  readonly villeLivraison: string | null;
  readonly codePostalLivraison: string | null;
  /** Vrai quand la ville de livraison est celle du CHANTIER, reprise par défaut (PRD v1.21). */
  readonly villeLivraisonParDefaut: boolean;
  /** La distance entre les deux villes, ou nulle avec le motif de son absence. */
  readonly km: string | null;
  readonly motifSansKm: string | null;
  /** Vrai quand ce km a été saisi et non déduit (073) ; `kmDeduit` dit ce que la table donne. */
  readonly kmEstForce: boolean;
  readonly kmDeduit: string | null;
};

/** Ce qu'un geste du panneau demande de changer. */
export type ChangementAcheminement = {
  duGroupe?: boolean;
  carriereId?: string | null;
  modeLivraison?: string | null;
  fournisseur?: string | null;
  communeProvenanceId?: string | null;
  villeProvenanceLibre?: string | null;
  communeLivraisonId?: string | null;
  kmForce?: string | null;
};

/**
 * D'où part ceci, et qui l'apporte.
 *
 * **Le mode d'abord**, à la demande expresse du métier : c'est lui qui commande le reste,
 * puisqu'une livraison assurée par le fournisseur n'appelle ni transporteur ni tournée. Les
 * champs suivants restent offerts quel qu'il soit — le métier n'a pas demandé qu'ils se
 * cachent, et un champ qui disparaît en cours de saisie fait perdre ce qu'on venait d'y
 * écrire.
 *
 * Sur une **ligne de contre-étude**, choisir un mode fait naître la pastille de livraison
 * (041) : c'est la seule des six réponses qui écrit ailleurs qu'ici, et l'écran l'annonce.
 */
export function Acheminement({
  porteur,
  carrieres,
  modifiable,
  enAttente,
  onModifier,
  apresGroupe,
}: {
  porteur: PorteurDAcheminement;
  carrieres: readonly Carriere[];
  modifiable: boolean;
  enAttente: boolean;
  onModifier: (changement: ChangementAcheminement) => void;
  /**
   * Ce qui s'affiche juste après la coche « groupe » — la case « figer » d'une rotation de
   * transport, là où Chris l'a demandée le 25/09/2026. Absent partout ailleurs.
   */
  apresGroupe?: JSX.Element;
}): JSX.Element {
  // Les sites retirés du référentiel ne sont plus proposés, mais celui que le porteur désigne
  // déjà reste dans la liste : sans lui, le menu afficherait « — » sur une ligne qui a
  // pourtant une provenance, et un clic ailleurs l'effacerait.
  const proposables = carrieres.filter((c) => c.actif || c.id === porteur.carriereId);
  const aRemplir = porteur.duGroupe && porteur.carriereId === null;

  return (
    <>
      <select
        className={`saisie${porteur.modeLivraison === null ? " saisie--a-remplir" : ""}`}
        value={porteur.modeLivraison ?? ""}
        disabled={!modifiable || enAttente}
        title="Qui achemine : le fournisseur livre, on affrète un transporteur, ou c'est par nos soins."
        onChange={(e) => {
          onModifier({ modeLivraison: e.target.value === "" ? null : e.target.value });
        }}
      >
        <option value="">— livraison ?</option>
        {MODES_LIVRAISON.map((m) => (
          <option key={m.code} value={m.code}>
            {m.libelle}
          </option>
        ))}
      </select>

      <label title="Le matériau vient d'un site du groupe — carrière, plateforme ou dépôt">
        <input
          type="checkbox"
          checked={porteur.duGroupe}
          disabled={!modifiable || enAttente}
          onChange={() => {
            /*
              DÉCOCHER EFFACE LE SITE ; COCHER NE DIT RIEN DU SITE.

              Décocher l'efface parce que les contraintes de la 010, de la 020, de la 039 et de
              la 041 l'exigent — un porteur décoché qui garderait son site continuerait
              d'apparaître au planning de production d'un site qui n'a plus rien à charger.

              Cocher, en revanche, ne doit **pas** envoyer `carriereId: null` : le serveur y lit
              un effacement explicite, et refuse alors de proposer le site le plus proche
              (08/09/2026). Les deux gestes partaient dans la même demande, si bien que la
              proposition ne se déclenchait jamais depuis l'écran — alors que la route la faisait
              correctement quand on l'appelait directement. **Trouvé en pilotant l'écran, aucun
              essai ne pouvait le voir** : chacun des deux côtés était juste.
            */
            onModifier(
              porteur.duGroupe
                ? { duGroupe: false, carriereId: null }
                : { duGroupe: true },
            );
          }}
        />
        {" groupe"}
      </label>
      {apresGroupe}

      {porteur.duGroupe && (
        <select
          className={`saisie${aRemplir ? " saisie--a-remplir" : ""}`}
          value={porteur.carriereId ?? ""}
          disabled={!modifiable || enAttente}
          title={aRemplir ? "Site de provenance à désigner" : "Site de chargement"}
          onChange={(e) => {
            onModifier({
              duGroupe: true,
              carriereId: e.target.value === "" ? null : e.target.value,
            });
          }}
        >
          <option value="">— site ?</option>
          {proposables.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code}
              {c.commune === null ? "" : ` · ${c.commune}`}
            </option>
          ))}
        </select>
      )}

      {/* Le fournisseur et le point de départ. Facultatifs — « la ville et le code postal de
          la ville de provenance SI ON LA CONNAÎT ». Rien ne les réclame. */}
      <ChampCourt
        valeur={porteur.fournisseur ?? ""}
        largeur="8rem"
        motif={/^.*$/}
        invite="fournisseur"
        videAutorise
        titre="Le fournisseur, en clair. Champ libre : rien ne le recalcule, et il n'y a pas de référentiel à tenir."
        modifiable={modifiable}
        enAttente={enAttente}
        onValider={(v) => {
          onModifier({ fournisseur: v === "" ? null : v });
        }}
      />
      {/* LES DEUX VILLES, CHOISIES AU RÉFÉRENTIEL DES TABLES DE DISTANCES (072).

          Elles étaient deux champs libres — un nom et un code postal tapés à la main — et la
          mesure a dit ce que ça produit : Brocas saisi 40240 quand le référentiel dit 40420,
          Escalans 40130 contre 40310, un « m2 », et une ligne où une phrase entière avait été
          tapée dans le champ ville. Le code postal ne se saisit plus : il vient de la commune.

          La ville de LIVRAISON est nouvelle. Avec celle de chargement, elle donne le km. */}
      {/* LA VILLE DE CHARGEMENT ACCEPTE UNE SAISIE (073), la ville de livraison non.

          *« Par nos soins choisi mais groupe pas coché : nous effectuons le transport mais
          le transport ne provient pas de nos sites, donc il faut pouvoir entrer un nom de
          ville qui n'existe pas dans nos tables distance. […] Dans ville livraison ce sera
          une ville existante que nous aurons créée »* (Chris, 08/09/2026).

          L'asymétrie est le fond de la règle : on charge n'importe où — chez un fournisseur
          du Lot, sur un chantier tiers —, mais on livre sur NOS chantiers, dont la commune
          est au référentiel et donne les zones et les distances. Ouvrir la saisie en
          livraison ferait perdre ce demi-trajet-là sans rien apporter. */}
      <ChoixCommune
        libelle="ville de chargement"
        communeId={porteur.communeProvenanceId}
        nom={porteur.villeProvenance}
        codePostal={porteur.codePostalProvenance}
        villeLibre={porteur.villeProvenanceLibre}
        onSaisirLibre={(nom) => {
          onModifier({ villeProvenanceLibre: nom });
        }}
        modifiable={modifiable && !enAttente}
        onChoisir={(communeId) => {
          onModifier({ communeProvenanceId: communeId });
        }}
      />
      <ChoixCommune
        libelle="ville de livraison"
        communeId={porteur.communeLivraisonId}
        nom={porteur.villeLivraison}
        codePostal={porteur.codePostalLivraison}
        /* La ville du chantier, reprise par défaut (PRD v1.21) : le champ l'affiche et le km s'en
           déduit, mais rien n'est enregistré — le retrait n'a donc rien à retirer. */
        parDefaut={porteur.villeLivraisonParDefaut}
        libelleRetrait="Revenir à la ville du chantier"
        modifiable={modifiable && !enAttente}
        onChoisir={(communeId) => {
          onModifier({ communeLivraisonId: communeId });
        }}
      />
      <Kilometrage
        km={porteur.km}
        motif={porteur.motifSansKm}
        estForce={porteur.kmEstForce}
        kmDeduit={porteur.kmDeduit}
        modifiable={modifiable}
        enAttente={enAttente}
        onForcer={(km) => {
          onModifier({ kmForce: km });
        }}
      />
    </>
  );
}
